import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AuthService, LogoutResult } from '@app/core/auth/auth.service';
import { Router } from '@angular/router';
import { DeviceService } from '@app/core/services/device.service';
import { Feature } from '@app/core/constants/feature-constants';
import { BlockScrollStrategy, GlobalPositionStrategy, Overlay, OverlayRef, ViewportRuler } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    // Slide in/out animation for the mobile sidebar
    trigger('openCloseMobileSidebar', [
      state('open', style({
        transform: 'translateX(100%)'
      })),
      state('closed', style({
        transform: 'translateX(0%)'
      })),
      transition('closed <=> open', [
        animate('0.2s linear')
      ])
    ])
  ]
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  public Feature = Feature;

  private mobileBreakpointSubscription: Subscription;
  private mobileSidebarPortal: TemplatePortal;

  public overlayRef: OverlayRef;

  /**
   * True if the desktop sidebar is hidden and the mobile sidebar is shown instead (if the open button was clicked)
   */
  public mobileSidebarActive = false;
  public mobileSidebarOpened = false;

  @ViewChild('mobileSidebar') public mobileSidebarRef: TemplateRef<unknown>;

  constructor(
    private router: Router,
    private auth: AuthService,
    public device: DeviceService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private breakpointObserver: BreakpointObserver,
    private viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Close mobile sidebar if we are not mobile anymore
    this.mobileBreakpointSubscription = this.breakpointObserver.observe('(min-width: 992px)')
    .subscribe((breakpointState) => {
      if (breakpointState.matches) {
        this.mobileSidebarActive = false;
        if (this.mobileSidebarOpened) {
          this.hideMobileSidebar();
        }
      } else {
        this.mobileSidebarActive = true;
      }
    });
  }

  ngOnInit(): void {
  }

  public ngOnDestroy() {
    // Cleanup sidebar in case it is still opened (to prevent the sidebar being uncloseable)
    this.hideMobileSidebar();

    this.mobileBreakpointSubscription?.unsubscribe();
  }

  public ngAfterViewInit() {
    this.mobileSidebarPortal = new TemplatePortal(this.mobileSidebarRef, this.viewContainerRef);
  }

  public showMobileSidebar() {
    // Do not open if not mobile
    if (this.breakpointObserver.isMatched('(min-width: 992px)')) {
      return;
    }

    const globalPositionStrategy = new GlobalPositionStrategy();
    // Set position so that the sidebar is not visible before the open animation moved the sidebar to the right
    // Offset = negative sidebar width
    globalPositionStrategy.left('-235px');

    this.overlayRef = this.overlay.create({
      positionStrategy: globalPositionStrategy,
      hasBackdrop: true,
      disposeOnNavigation: true,
      scrollStrategy: new BlockScrollStrategy(this.viewportRuler, this.document),
      height: this.viewportRuler.getViewportSize().height
    });

    this.overlayRef.attach(this.mobileSidebarPortal);
    this.overlayRef.backdropClick().subscribe(() => {
      this.hideMobileSidebar(false);
    });

    this.overlayRef.detachments().subscribe(() => {
      // After dispose the hide method may not be called. Therefore we need to cleanup here
      if (this.mobileSidebarOpened) {
        this.mobileSidebarOpened = false;
      }
    });

    /* Update height of the mobile sidebar if the viewport is resized.
     * CSS styles are not sufficient because the URL bar on mobile browsers prevent scrolling to the
     * bottom of the sidebar in some cases.
     */
    this.viewportRuler.change().subscribe(() => {
      this.overlayRef.updateSize({height: this.viewportRuler.getViewportSize().height});
    });

    this.mobileSidebarOpened = true;

    // Focus the first anchor if the menu is opened.
    // Then, the focus trap takes care that the focus stays inside the sidebar
    this.overlayRef.overlayElement.querySelector('a').focus();
  }

  /**
   * Closes the mobile sidebar.
   * If detach is set to true (default), the close animation is not shown.
   * @param detach Set this to false if the detachment of the overlay should happen after the end of the close animation
   */
  public hideMobileSidebar(detach: boolean = true) {
    this.mobileSidebarOpened = false;

    if (detach) {
      this.overlayRef?.detach();
    }
  }

  public onNavigationClick(event: MouseEvent) {
    // Close mobile sidebar if an anchor element was clicked
    if (this.mobileSidebarOpened) {
      // Also check SVGElement because of FontAwesome icons
      if (event.target instanceof HTMLElement || event.target instanceof SVGElement) {
        let el = event.target;
        while (!(el instanceof HTMLAnchorElement) && el.parentElement && !(el.parentElement instanceof HTMLUListElement)) {
          el = el.parentElement;
        }

        // Only close sidebar if an anchor element was clicked (i.e. any of the target's parent or the target itself is an anchor)
        if (el instanceof HTMLAnchorElement) {
          this.hideMobileSidebar();
        }
      }
    }
  }

  public onLogoutClick() {
    this.hideMobileSidebar();
    this.auth.logout();
  }

}
